'use client'

/**
 * Documentation section for the 3M Scotchgard Paint Protection Film Pro
 * Series 200 pair: /products/3m-scotchgard-ppf-pro-series-200-gloss and
 * /products/3m-scotchgard-ppf-pro-series-200-matte. Three official 3M
 * sources Peter supplied (scripts/supplier-assets/intake-scotchgard-200-docs/),
 * converted to HTML rather than embedded as PDFs or offered as download-only
 * links. Follows the pattern established in hexis-bodyfence.tsx: gloss and
 * matte are the same manufacturer product family, differing only by surface
 * finish, so the shared content (Typical Properties/Specifications,
 * Installation Guide, Warranty) is factored into internal, non-exported
 * helper functions used by both exported wrapper components, rather than
 * duplicated JSX. No colour swatch poster: this is a clear/transparent film
 * with a finish choice, not a colour catalog, so this file has no
 * poster/Modal image section, matching 3m-2080.tsx's approach when a poster
 * is absent.
 *
 * Source files (see scripts/supplier-assets/intake-scotchgard-200-docs/sources.json
 * for full provenance):
 * - scotchgard-200-typical-properties.txt (3M product detail page, "Typical
 *   Properties" + "Dimensions and Classifications" tables plus marketing
 *   highlights)
 * - scotchgard-200-installation-guide.txt (3M Paint Protection Film
 *   Installation Guidelines, covering four film series -- Pro Series 200,
 *   150, 100, and 50 -- in shared tables)
 * - scotchgard-200-warranty-card.txt (3M Paint Protection Film Vehicle Owner
 *   Warranty card, covering several PPF product lines in one shared table)
 *
 * Two PDFs referenced from the same 3M product page were deliberately NOT
 * used, per sources.json's "notUsed" section: the consumer marketing
 * brochure (76-5100-2923-6), which is mostly ad copy with only one figure of
 * interest (the "8 mil" rounded thickness, cross-referenced below) and is too
 * thin/promotional to stand as its own section; and the companion "2319229O"
 * installation-guideline PDF, which duplicates the Installation Guidelines
 * content actually used but extracted with corrupted/garbled paragraphs in
 * its Maintenance section.
 *
 * Finish scoping (gloss vs. matte): none of the three source files
 * differentiate any figure, table, or instruction by finish, apart from the
 * Typical Properties table's single "Surface Finish: Glossy, Matte" row,
 * which lists both finishes together rather than giving each its own set of
 * values. Every other spec, dimension, installation step, and warranty term
 * applies identically to both finishes. Per the source data, there is
 * nothing to differentiate ScotchgardPro200GlossDocs and
 * ScotchgardPro200MatteDocs on, so both exported components render the same
 * shared sections with no invented per-finish content.
 *
 * Multi-series scoping (Installation Guide): the Installation Guidelines PDF
 * covers Pro Series 200 alongside 3M Series 150, 100, and 50 in shared
 * tables, and the source intake file already flags which rows apply. Only
 * Pro Series 200 values are used here:
 * - Slip Solution (Table B): only the Pro Series 200 row (Johnson's Baby
 *   Shampoo 2-4 ml, Dawn dish soap 1-2 ml per 32 oz / 1 liter) is shown. The
 *   source notes Series 150 shares this same ratio and Series 100/50 use a
 *   lighter ratio; neither is reproduced here since both are out of scope.
 * - Waxing and Alternatives: only the "150 and 200" row (3M Perfect-It Show
 *   Car Paste Wax within three to six months) is shown; no other series row
 *   exists in this section of the source.
 * - Tack Solution (Table C), Hybrid Solution (Table D), Application
 *   Characteristics (Table A), vehicle prep, installation notes, and
 *   maintenance/cleaning/removal content are general to the Installation
 *   Guidelines document (not broken out per series in the source), so they
 *   are reproduced as-is.
 *
 * Warranty-card scoping: the warranty card covers several 3M PPF product
 * lines in one shared table. Only the "Scotchgard Pro Series / Matte | Gloss
 * | 200 Series Gloss" bucket (10-year coverage: yellowing, bubbling,
 * cracking) applies to these two products and is shown below. The Series 100
 * Gloss (7-year), Scotchgard PPF non-Pro-Series (5-year), and 3M
 * VentureShield Paint Protection Film (5-year) rows are different product
 * lines, out of scope, and are not shown or attached to these products.
 *
 * Figure discrepancy (7.9 mil vs. 8 mil): the Typical Properties /
 * Dimensions and Classifications table gives film thickness as 7.9 mil
 * (0.2 mm); the consumer brochure separately states "8 mil" as a rounded
 * marketing figure. Both figures are shown below, per the source note's
 * instruction: 7.9 mil is presented as the primary figure (it is the
 * technical/dimensional data source), with the brochure's rounded 8 mil
 * figure noted alongside rather than silently reconciled or dropped.
 *
 * (TM)/(R) restoration: the source text files use "(TM)" and "(R)" as plain-
 * text placeholder markers for the trademark and registered-trademark
 * symbols (an extraction artifact), written that way for every occurrence of
 * "3M(TM)", "Scotchgard(TM)", "Perfect-It(TM)", "VentureShield(TM)",
 * "Johnson's(R)", "Dawn(R)", "Scotch-Brite(R)", "Meguiar's(R)", and "Fusion
 * Tools(R)". These are restored to the proper Unicode symbols (3M(TM) ->
 * 3M(tm-symbol), etc.) throughout this file rather than printed literally as
 * "(TM)"/"(R)".
 *
 * Em-dash substitutions: no literal em-dash character (U+2014) appears
 * anywhere in the three source files (grep-confirmed). The source files do
 * use plain ASCII double-hyphens ("--") as a dash stand-in in two places
 * inside actual document content (both in the Installation Guide's
 * Maintenance/Waxing sections; the other "--" occurrences in the source
 * files are the intake author's own file-header/note commentary, not 3M
 * document content, and are not transcribed verbatim). Per house style,
 * both are rewritten as introducing-an-explanation constructions using a
 * colon rather than a dash:
 * - "If a pressure washer is used, strict guidelines must be followed --
 *   aggressive washing can damage the film, with excessive pressure..." ->
 *   "If a pressure washer is used, strict guidelines must be followed:
 *   aggressive washing can damage the film, with excessive pressure..."
 * - "Avoid using any wax that contains dyes, abrasive polishes, and/or
 *   rubbing compounds -- abrasive particles will damage the PPF surface..."
 *   -> "Avoid using any wax that contains dyes, abrasive polishes, and/or
 *   rubbing compounds: abrasive particles will damage the PPF surface..."
 *
 * Duplicate/omitted content (same restraint as 3m-2080.tsx/hexis-bodyfence.tsx):
 * - The Installation Guide's closing "Warranty, Limited Remedy, and
 *   Disclaimer" legal boilerplate is generic 3M language (identical in
 *   substance to the disclaimer in 3m-2080.tsx's Product Bulletin group),
 *   not the product-specific 10-year warranty. It is kept once, as the
 *   closing subsection of the Installation Guide group (matching the
 *   source's own instruction that this Documentation section is standalone
 *   and should include it), while the dedicated, product-specific Warranty
 *   section below is built entirely from the Warranty Card file.
 * - The Warranty Card's file-header note supplying the parenthetical "-- this
 *   is our product's row" annotation, and its "(For reference only, NOT
 *   applicable to our products: ...)" aside about the Series 100 Gloss /
 *   non-Pro-Series / VentureShield rows, are the intake author's own scoping
 *   notes, not 3M warranty-card text; they are represented here as this
 *   file's own scoping caption rather than transcribed as if they were part
 *   of the warranty card itself.
 * - Running file-header lines (SOURCE/TITLE/FETCHED/NOTE metadata at the top
 *   of each intake .txt file) are intake bookkeeping, not document content,
 *   and are not reproduced; each source's control identifier (e.g. the
 *   warranty claim phone numbers, the 3M product page) is kept inline where
 *   the source text itself states it.
 *
 * No figure, unit, or word was otherwise added, removed, or rounded.
 *
 * See scripts/supplier-assets/intake-scotchgard-200-docs/sources.json for
 * full provenance.
 */
import { Disclosure, DisclosureGroup } from '@heroui/react'
import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'

// ---------------------------------------------------------------------
// Presentational helpers (same shape as avery-sw900.tsx / 3m-2080.tsx)
// ---------------------------------------------------------------------

function KeyValueTable({ rows }: { rows: Array<{ label: string; value: ReactNode }> }) {
  return (
    <div className='rounded-lg border border-divider overflow-hidden'>
      {rows.map((row, i) => (
        <div
          key={row.label}
          className={`flex flex-col gap-1 px-4 py-2.5 text-sm sm:flex-row sm:gap-6 ${i % 2 === 0 ? 'bg-overlay' : ''}`}
        >
          <span className='text-muted font-medium shrink-0 sm:w-2/5'>{row.label}</span>
          <span className='text-foreground'>{row.value}</span>
        </div>
      ))}
    </div>
  )
}

function BulletList({ items }: { items: ReactNode[] }) {
  return (
    <ul className='list-disc space-y-1.5 pl-5 text-foreground/80'>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  )
}

interface DocSectionProps {
  id: string
  title: string
  children: ReactNode
}

function DocSection({ id, title, children }: DocSectionProps) {
  return (
    <Disclosure id={id} aria-label={title} className='overflow-hidden rounded-lg border border-divider bg-surface'>
      <Disclosure.Heading>
        {/* transform-none: HeroUI scales a pressed button to 0.97, which on a
            full-width trigger inside an overflow-hidden card leaves a sliver of
            card showing around the pressed fill. cw-ui-heading: the trigger
            sits in the Disclosure's h3, which the theme sets in Anton; the
            title stays in the body face (spec 3.2 component-heading opt-out). */}
        <Button
          slot='trigger'
          variant='ghost'
          className='h-auto w-full justify-between rounded-none px-4 py-3.5 text-left transform-none'
        >
          <span className='cw-ui-heading text-base font-semibold text-foreground'>{title}</span>
          <Disclosure.Indicator className='text-muted' />
        </Button>
      </Disclosure.Heading>
      <Disclosure.Content>
        <Disclosure.Body className='space-y-3 border-t border-divider px-4 py-4 text-sm text-foreground/80'>
          {children}
        </Disclosure.Body>
      </Disclosure.Content>
    </Disclosure>
  )
}

// ---------------------------------------------------------------------
// Typical Properties / Specifications: shared across both finishes
// (scotchgard-200-typical-properties.txt)
// ---------------------------------------------------------------------

function TypicalPropertiesSection() {
  return (
    <div>
      <h3 className='text-base font-semibold text-foreground mb-3'>Typical Properties &amp; Specifications</h3>
      <p className='text-xs text-muted mb-3'>3M product detail page, Scotchgard™ Paint Protection Film Pro Series 200</p>
      <DisclosureGroup allowsMultipleExpanded className='flex flex-col gap-3'>
        <DocSection id='tp-highlights' title='Highlights'>
          <BulletList
            items={[
              'Scotchgard™ Paint Protection Film Pro Series 200 is the newest paint protection film designed to protect vehicle paint from rock chips, scratches and the elements',
              'Available in matte and gloss finishes',
              'Smooth top surface with hydrophobic features for easy maintenance',
              'Gloss finish has excellent clarity',
              'Optimized adhesive for repositioning, minimizing lift lines and adhesive marks',
              'Simple release liner removal',
              'Ability to stretch for bulk installations',
              'Self-healing technology to remove minor scratches or marks with heat applied to the film surface',
              'Professionally installed by 3M authorized installers',
              'Approved to be used with 3M™ Pattern Marketplace, the latest innovative digital pattern software with nearly 4,500 quality-tested paint protection film and auto window film patterns going back more than ten model years',
              'Low orange peel',
              '10-year consumer warranty backed by 3M (ask your 3M authorized installer or see the warranty card for complete details)'
            ]}
          />
          <p>
            Designed to protect automotive paint and other vulnerable surfaces of the vehicle from stone chips,
            scratches, bug damage, road tar, stains, automotive fluid stains and punishing outdoor weathering
            including harsh ultraviolet (UV) radiation. Available in 72-inch width film for a seamless installation
            on larger panel areas of the vehicle like full hood coverage.
          </p>
        </DocSection>

        <DocSection id='tp-typical-properties' title='Typical Properties'>
          <KeyValueTable
            rows={[
              { label: 'Adhesive Type', value: 'PSA' },
              { label: 'Application', value: 'Paint Protection, Scratch Protection, Surface Protection' },
              { label: 'Backing Material', value: 'PET' },
              { label: 'Base Film', value: 'Polyurethane' },
              { label: 'Brands', value: 'Scotchgard™' },
              { label: 'Film Coating', value: 'Clear Coat' },
              { label: 'Industries', value: 'Automotive' },
              { label: 'Liner Color', value: 'Clear' },
              { label: 'Liner Material', value: 'PET' },
              { label: 'Product Color', value: 'Transparent' },
              { label: 'Product Series', value: '200' },
              { label: 'Product Usage', value: 'Paint Protection' },
              { label: 'Surface Finish', value: 'Glossy, Matte' },
              { label: 'Warranty Period', value: '10 yr' }
            ]}
          />
        </DocSection>

        <DocSection id='tp-dimensions' title='Dimensions and Classifications'>
          <KeyValueTable
            rows={[
              {
                label: 'Film Thickness',
                value: (
                  <>
                    7.9 mil (0.2 mm)
                    <br />
                    <span className='text-xs text-muted'>
                      The consumer brochure separately rounds this figure to 8 mil; 7.9 mil is the primary figure
                      here, from this technical dimensions table.
                    </span>
                  </>
                )
              },
              { label: 'Overall Length', value: '50 ft, 100 ft (15.24 m, 30.5 m)' },
              { label: 'Overall Width', value: '24 in, 30 in, 36 in, 60 in, 72 in (0.61 m, 0.76 m, 0.91 m, 1.52 m, 1.83 m)' }
            ]}
          />
        </DocSection>
      </DisclosureGroup>
    </div>
  )
}

// ---------------------------------------------------------------------
// Installation Guide: shared across both finishes
// (scotchgard-200-installation-guide.txt; Pro Series 200 rows only, see
// the top comment block for the multi-series scoping decisions)
// ---------------------------------------------------------------------

function InstallationGuideSection() {
  return (
    <div>
      <h3 className='text-base font-semibold text-foreground mb-3'>Installation Guide</h3>
      <p className='text-xs text-muted mb-3'>
        3M™ Paint Protection Film Installation Guidelines. Scoped to the Scotchgard™ Paint Protection Film Pro
        Series 200 rows in this shared document; the Series 150, 100, and 50 rows are out of scope and not shown.
      </p>
      <DisclosureGroup allowsMultipleExpanded className='flex flex-col gap-3'>
        <DocSection id='ig-overview' title='Overview'>
          <p>
            This installation guide provides details on the proper installation of 3M™ Paint Protection Films
            (PPF). Included in this document are instructions on the creation of proper installation solutions,
            installation guidelines, and recommendations for the maintenance and cleaning of paint protection film
            after installation.
          </p>
        </DocSection>

        <DocSection id='ig-application-characteristics' title='Application Characteristics'>
          <KeyValueTable
            rows={[
              { label: 'Application Method', value: 'Wet only' },
              { label: 'Substrate Types', value: 'OEM painted surfaces*' },
              { label: 'Application Temperatures', value: '60F to 85F (16C to 30C)' }
            ]}
          />
          <p className='text-xs text-muted'>* Check adhesive bond to powder-coated or water-based paints.</p>
          <p className='text-xs text-muted'>
            NOTE: Films are NOT recommended for use on stainless steel substrates, and are NOT warranted for
            stainless steel applications. Contact your local 3M sales representative for information on appropriate
            product for such applications.
          </p>
        </DocSection>

        <DocSection id='ig-installation-solutions' title='Installation Solutions'>
          <p className='font-medium text-foreground'>Slip Solution</p>
          <p>
            Slip solution must be prepared freshly every day. Do NOT store them overnight. Slip solution can be made
            with either Johnson&rsquo;s® Baby Shampoo or Dawn® dish soap.
          </p>
          <KeyValueTable
            rows={[
              { label: "Johnson's® Baby Shampoo", value: '2 ml to 4 ml per 32 oz (1 liter) of DI or filtered water' },
              { label: 'Dawn® Dish Soap', value: '1 to 2 ml per 32 oz (1 liter) of DI or filtered water' }
            ]}
          />
          <p className='text-xs text-muted'>
            The suggested slip solution ratios are only starting points. The appropriate level of shampoo or soap
            for a slip solution depends on the parts to be installed and the installation environment. Always keep
            the paint protection film wet when working in hot environments, and installers may need to increase the
            non-detergent soap content in the slip solution.
          </p>

          <p className='font-medium text-foreground pt-2'>3M™ Paint Protection Film Installation Gel</p>
          <p>
            A slip foundation alternative to water and shampoo/soap solutions that can be utilized with all 3M™
            Paint Protection Films to provide additional working time. It is a high viscosity water-based gel that
            arrives ready-to-use.
          </p>

          <p className='font-medium text-foreground pt-2'>Tack Solution</p>
          <KeyValueTable
            rows={[
              { label: '99% Isopropyl Alcohol', value: '150 ml IPA to 850 ml DI or filtered water' },
              { label: '70% Isopropyl Alcohol', value: '250 ml IPA to 750 ml DI or filtered water' }
            ]}
          />

          <p className='font-medium text-foreground pt-2'>Hybrid Solution</p>
          <p>Tack solution made with BOTH Johnson&rsquo;s® Baby Shampoo and IPA:</p>
          <KeyValueTable
            rows={[
              {
                label: '99% Isopropyl Alcohol',
                value: "2 ml Johnson's + 150 ml IPA + 850 ml DI or filtered water"
              },
              {
                label: '70% Isopropyl Alcohol',
                value: "2 ml Johnson's + 250 ml IPA + 750 ml DI or filtered water"
              }
            ]}
          />
        </DocSection>

        <DocSection id='ig-installation-tools' title='Installation Tools'>
          <BulletList
            items={[
              'Disposable 5 ml syringes',
              'Isopropyl alcohol',
              "Johnson's® Baby Shampoo or Dawn® dish soap",
              'DI or filtered water',
              'Three one liter spray bottles',
              '3M™ General Purpose Adhesive Cleaner',
              'Heat gun',
              'Scotch-Brite® Microfiber Cloths',
              "3M™ Perfect-It™ Cleaner Clay or Meguiar's® C2000 Pro Detailing Clay (Mild)",
              '3M™ Surface Preparation System (EU only)',
              'Optional: 3M™ Paint Protection Film Installation Gel',
              'Fusion Tools® Turbo Squeegees',
              'Cutting Knife with 30-degree blades'
            ]}
          />
        </DocSection>

        <DocSection id='ig-cap-sheet-removal' title='Cap Sheet Removal'>
          <p>
            3M™ Paint Protection Films may come with cap sheet depending on the region. The cap sheet layer helps
            protect the film and is easily removed. Cap sheets are removable by hand and must be removed prior to
            installation and plotter cutting.
          </p>
          <p className='text-xs text-muted'>
            NOTE: Roll up the film firmly, ensuring no slack to prevent possible tunneling or buckling. After
            cutting any film for bulk parts or plotter cutting, add tape within six inches of each roll edge and in
            middle of the roll to prevent the film from unwinding. Stand partial rolls straight up and down on rack
            or similar.
          </p>
        </DocSection>

        <DocSection id='ig-vehicle-prep' title='Vehicle Preparation Instructions'>
          <BulletList
            items={[
              'Ensure there is no ceramic coating applied to the vehicle paint. If there is, work with an authorized paint correction specialist or automotive detailer to remove the coating.',
              "Use 3M™ General Purpose Adhesive Cleaner and clean Scotch-Brite® Microfiber Cloths to remove any surface residue (e.g. wax, tar, paint sealant).",
              'Use a non-ammonia foaming glass cleaner and clean Scotch-Brite® Microfiber Cloths to remove any excess adhesive cleaner.',
              "Use 3M™ Perfect-It™ Clay Bar or Meguiar's® C2000 pro detailing clay to remove surface contaminants on the painted surface.",
              'Wipe down the vehicle with clean Scotch-Brite® Microfiber Cloths and do final inspection prior to installation.'
            ]}
          />
        </DocSection>

        <DocSection id='ig-installation-notes' title='Installation Notes'>
          <p className='text-xs text-muted'>
            NOTE: For bulk installations, dry any areas that may have been exposed to excess slip solution when
            making anchor points on an install such as when stretching four corners for a bulk hood install.
          </p>
          <BulletList
            items={[
              'Wipe off, flush out, and dry off any slip solution in non-active area anchor points on the bumper or fender as you stretch the PPF over the hood and lock it in place.',
              'Hold the film in place for ten seconds to activate the firmer adhesive.',
              'Stretch out any fingers.',
              'Lock the four anchor points in place with warm water or tack solution.',
              'Flush out slip solution on edges and hold down to activate and lock the edges.',
              'Spray the film edges with tack solution.',
              'Gently wipe down film, dry, and inspect.',
              'Allow 15 minutes for adhesive to dry before putting pressure on film to avoid leaving marks.',
              'Heat set all film edges with a heat gun at approximately 180F (82C) after completing the installation.'
            ]}
          />
        </DocSection>

        <DocSection id='ig-complex-curves' title='Complex Curves'>
          <p>
            For application over complex curves, activate the adhesive with &ldquo;Tack Solution&rdquo; and hold
            the film in place for 5 to 10 seconds. Warm water can be utilized in solutions to assist as needed with
            installation.
          </p>
        </DocSection>

        <DocSection id='ig-application-temperature' title='Application Temperature'>
          <p>
            Environmental and vehicle application temperatures of 60F to 85F (16C to 30C) can be used. However, 3M
            recommends 70F to 79F (21C to 26C) as the optimal application temperature.
          </p>
        </DocSection>

        <DocSection id='ig-maintenance-cleaning-removal' title='Maintenance, Cleaning, and Removal'>
          <BulletList
            items={[
              '3M recommends waiting at least 72 hours after film installation before washing the vehicle.',
              'Customers should inspect their vehicle after film installation, and return the vehicle to the installer if any lifting or peeling occurs.',
              'Touchless car wash and hand washing are the preferred methods for washing vehicles with PPF.',
              'Loose paint protection film could tear away from a moving vehicle. After washing, check the entire installation film carefully for edge lifting. Repair, remove, or replace damaged film.',
              'Clean bird droppings, tar, and bugs off of the film as soon as possible to avoid staining or damage.',
              <>
                If a pressure washer is used, strict guidelines must be followed: aggressive washing can damage the
                film, with excessive pressure forcing water underneath the film and weakening the adhesion strength
                to the paint surface, causing the film to lift or curl. These problems are magnified by wind. To
                avoid edge lifting or other damage:
                <ul className='list-disc space-y-1 pl-5 mt-1.5'>
                  <li>Use a spray nozzle with a minimum 40-degree wide spray pattern.</li>
                  <li>Ensure the spray nozzle includes a nozzle protector (tip guard).</li>
                  <li>Use a maximum pressure of 80 bar.</li>
                  <li>If the system is heated, limit the water temperature to 140F (60C) or less.</li>
                  <li>
                    Hold the nozzle at least 12 in. (30 cm) away from and perpendicular (90 degrees +/- 10) to the
                    paint protection film, or a minimum of 39 in. (1 m) away from the graphic at a 30-degree angle or
                    higher.
                  </li>
                  <li>Do NOT direct the water stream at between 0 to 60 degrees relative to the edge of the paint protection film.</li>
                </ul>
              </>,
              'The surface of the paint protection film is coated with a clear coat, protecting it to a high degree against environmental influences and mechanical stress.',
              'Wash the vehicle in a shaded and/or protected area with a neutral shampoo.',
              'Cleaning with strong alkaline, acidic, or alcoholic cleaning agents should be avoided, as permanent damage to the film may occur.',
              'Do NOT use abrasive cleaning pads, brushes, and similar products, as doing so will permanently damage the film.',
              '3M recommends drying the car quickly after cleaning to help prevent hard water spot/chlorine stains. Applying a coating of liquid wax after cleaning also helps prevent hard water spot/chlorine stains.'
            ]}
          />
        </DocSection>

        <DocSection id='ig-waxing' title='Waxing and Alternatives'>
          <p>
            Avoid using any wax that contains dyes, abrasive polishes, and/or rubbing compounds: abrasive particles
            will damage the PPF surface and result in a loss of surface quality.
          </p>
          <p>
            For Scotchgard™ Paint Protection Film Pro Series 200: within three to six months of PPF installation,
            apply 3M™ Perfect-It™ Show Car Paste Wax (PN39526). Users can also apply 3M™ Ceramic Coating (PN39901)
            or 3M™ Ceramic Boost Spray (PN39905) to add an additional hydrophobic coating for easy clean features
            and additional stain resistance.
          </p>
          <p>
            Use of waxes other than those described above is on a user test-and-approve basis only. Always test the
            coating in an inconspicuous area before applying to the surface of the paint protection film. After
            waxing, remove any wax build-up along film edges with a lint-free cloth.
          </p>
        </DocSection>

        <DocSection id='ig-removal' title='Removal'>
          <p>
            3M recommends using a steamer or heat gun to aid in removal of PPF. Remove the film at a 30 to 60 degree
            angle relative to the surface of the paint. 3M recommends hiring a PPF installation professional for
            this process.
          </p>
        </DocSection>

        <DocSection id='ig-performance-life' title='Factors Affecting Paint Protection Film Performance Life'>
          <BulletList
            items={[
              'Selection, condition, and preparation of the substrate',
              'Surface texture',
              'Application methods',
              'Environmental conditions'
            ]}
          />
        </DocSection>

        <DocSection id='ig-health-safety' title='Health and Safety'>
          <p>
            When handling any chemical products, read the manufacturers&rsquo; container labels and the safety data
            sheets (SDS) for important health, safety, and environmental information. When using any equipment,
            always follow the manufacturer&rsquo;s instructions for safe operation.
          </p>
        </DocSection>

        <DocSection id='ig-warranty-disclaimer' title='Warranty, Limited Remedy, and Disclaimer'>
          <p className='text-xs uppercase text-muted'>
            Unless a different warranty is specifically stated on the applicable 3M product packaging or product
            literature (in which case such warranty governs), 3M warrants that each 3M product meets the applicable
            3M product specification at the time 3M ships the product. 3M makes no other warranties or conditions,
            express or implied, including, but not limited to, any warranty or condition of merchantability,
            fitness for a particular purpose, or arising out of a course of dealing, custom, or usage of trade. If a
            3M product does not conform to this warranty, the sole and exclusive remedy is, at 3M&rsquo;s option,
            replacement of the 3M product or refund of the purchase price.
          </p>
          <p className='text-xs uppercase text-muted'>
            Except for the limited remedy stated above, and except to the extent prohibited by law, 3M will not be
            liable for any loss or damage arising from or related to the 3M product, whether direct, indirect,
            special, incidental, or consequential (including, but not limited to, lost profits or business
            opportunity), regardless of the legal or equitable theory asserted, including, but not limited to,
            warranty, contract, negligence, or strict liability.
          </p>
        </DocSection>
      </DisclosureGroup>
    </div>
  )
}

// ---------------------------------------------------------------------
// Warranty: shared across both finishes (scotchgard-200-warranty-card.txt,
// "Scotchgard Pro Series / Matte | Gloss | 200 Series Gloss" row only)
// ---------------------------------------------------------------------

function WarrantySection() {
  return (
    <div>
      <h3 className='text-base font-semibold text-foreground mb-3'>Warranty</h3>
      <p className='text-xs text-muted mb-3'>
        3M™ Paint Protection Film Vehicle Owner Warranty card, Scotchgard™ Pro Series (Matte | Gloss | 200 Series
        Gloss) row only. The Series 100 Gloss (7-year), Scotchgard™ Paint Protection Film non-Pro-Series (5-year),
        and 3M™ VentureShield™ Paint Protection Film (5-year) rows on the same card cover different product lines
        and do not apply to these two products.
      </p>
      <DisclosureGroup allowsMultipleExpanded className='flex flex-col gap-3'>
        <DocSection id='w-overview' title='3M™ Paint Protection Film Vehicle Owner Warranty'>
          <p>
            3M designs, formulates and manufactures 3M™ Paint Protection Film for optimum performance and
            durability. The 3M commitment to quality is the foundation for the warranty coverage we provide.
          </p>
          <p>
            3M warrants that each 3M product meets the applicable 3M product specification at the time 3M ships the
            product. In addition, 3M warrants, for Scotchgard™ Pro Series (Matte | Gloss | 200 Series Gloss):
          </p>
          <BulletList items={['10-year warranty coverage', 'Yellowing', 'Bubbling', 'Cracking']} />
        </DocSection>

        <DocSection id='w-exclusions' title='What Is Not Covered'>
          <p>
            This warranty does not apply to damage caused by misuse, accident, misapplication or ordinary wear, nor
            does it apply to dents caused by physical impact of rocks or road debris. Warranty coverage does not
            include paint pull during film removal process. This warranty is only provided to the original consumer
            purchaser and is not transferable. This warranty does not apply to headlights.
          </p>
          <p>The following warranty is only valid in the United States and Canada.</p>
        </DocSection>

        <DocSection id='w-registration' title='Registration'>
          <p>
            Register your warranty online to validate at 3m.com/ppfwarranty. By entering the information into 3M&rsquo;s
            warranty registration system, you consent to allow 3M to share with Carfax the VIN number and
            installation date. Personal information (name, address, phone number, etc.) will NOT be included in the
            submission to Carfax. Some installers may choose to offer, on their own behalf, different or additional
            warranties or remedies, beyond the limited warranty and limited remedy offered by 3M.
          </p>
        </DocSection>

        <DocSection id='w-claim' title='Making a Warranty Claim'>
          <p>
            To make a claim under this warranty in the United States, first contact your installer. If the
            installer is not available, contact 3M at 1-800-643-0955. 3M will direct you to an installer selected by
            3M to have your 3M™ Paint Protection Film inspected and, if your claim is covered, 3M will have the
            installer provide the stated remedy. In Canada, first contact your installer; if not available, call
            1-800-364-3577.
          </p>
        </DocSection>

        <DocSection id='w-implied-warranty' title='Implied Warranty Limitation'>
          <p>
            All implied warranties and conditions, including but not limited to the implied warranty or condition of
            merchantability, the implied warranty or condition of fitness for a particular purpose, and any implied
            warranty or condition arising out of a course of dealing or of performance, custom or usage of trade,
            are limited in duration to the applicable warranty time period. Some states&rsquo; jurisdictions do not
            allow limitations on how long an implied warranty lasts, so the above limitation may not apply to you.
          </p>
        </DocSection>

        <DocSection id='w-limitation-of-liability' title='Limitation of Liability'>
          <p>
            Except where prohibited by law, 3M and the installer of the 3M product shall not be liable for any loss
            or damage arising from the 3M product, whether direct, indirect, special, incidental or consequential,
            regardless of the legal theory asserted, including warranty, contract, negligence or strict liability.
            Some states do not allow the exclusion or limitation of incidental or consequential damages, so the
            above limitation or exclusion may not apply to you.
          </p>
        </DocSection>
      </DisclosureGroup>
    </div>
  )
}

// ---------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------

export function ScotchgardPro200GlossDocs() {
  return (
    <div className='space-y-8'>
      <TypicalPropertiesSection />
      <InstallationGuideSection />
      <WarrantySection />
    </div>
  )
}

export function ScotchgardPro200MatteDocs() {
  return (
    <div className='space-y-8'>
      <TypicalPropertiesSection />
      <InstallationGuideSection />
      <WarrantySection />
    </div>
  )
}
